import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import moment from 'moment-timezone';

// types
import {
	ICreateOpportunityRequest,
	IOpportunityResponse,
	IUpdateOpportunityRequest,
	OPPORTUNITY_STATUSES,
} from '@joc/api-gateway';

// redux
import { Store } from 'redux/root';
import { selectorGetError } from 'redux/error-service/selector';
import { selectorGetUserId } from 'redux/user-service/selector';
import { resetError, setError } from 'redux/error-service/action';
import { createOpportunity, updateOpportunity } from 'redux/opportunities-service/action';

// components
import ResponseFailure from 'shared/components/ResponseFailure';
import PopupCongrats from '../PopupCongrats';
import OpportunityForm from '../../OpportunityForm';
import { useBatchFileUpload } from 'hooks/useBatchFileUpload';
import { UploadProgressSnackbar } from 'shared/components/UploadProgressSnackbar';

type PopupUpdateParentProps = {
	setIsShowUpdateOpportunityPopup: React.Dispatch<React.SetStateAction<boolean>>;
	opportunityData: IOpportunityResponse & { opportunityPoints?: number; volunteerRank?: number };
};

const PopupUpdate: FC<PopupUpdateProps> = ({
	setIsShowUpdateOpportunityPopup,
	setOppoError,
	resetOppoError,
	error,
	opportunityData,
	updateOppo,
	userId,
}: PopupUpdateProps) => {
	// const [isVirtualOpportunity, setIsVirtualOpportunity] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { items: batchUploadItems, uploadBatch, isSubmitting: isUploadingAttachments, abort } = useBatchFileUpload();

	useEffect(() => {
		return () => {
			if (isUploadingAttachments) {
				abort();
			}
		};
	}, [isUploadingAttachments, abort]);

	const initialValues = useMemo(() => {
		const startDate = moment.tz(opportunityData.startDate, moment.tz.guess());

		return {
			opportunityName: opportunityData.opportunityName,
			opportunityDescription: opportunityData.opportunityDescription,
			isApprovalRequired: opportunityData.isApprovalRequired || false,
			startDate: new Date(opportunityData.startDate),
			endDate: new Date(opportunityData.endDate),
			startTime: startDate.clone().format('HH:mm'),
			endTime: opportunityData?.duration
				? startDate
						.clone()
						.add(opportunityData.duration, 'seconds')
						.utcOffset(startDate.utcOffset())
						.format('HH:mm')
				: moment(opportunityData.endDate).format('HH:mm'),
			user: opportunityData?.user?.id ? +opportunityData?.user?.id : userId,
			withWho: [],
			opportunityType: opportunityData.opportunityType,
			opportunityPoints: opportunityData?.opportunityPoints,
			volunteerRank: opportunityData?.volunteerRank,
			isVirtual: opportunityData.isVirtual,
			address: opportunityData?.address,
			opportunityUrl: opportunityData?.opportunityUrl,
			attachments: (opportunityData.attachments as unknown as string[]) || [],
			pendingAttachmentFiles: [] as File[],
		};
	}, [opportunityData]);

	const submitClickHandler = async (values: typeof initialValues): Promise<void> => {
		setIsLoading(true);
		try {
			let uploadedAttachmentKeys: string[] = [];
			if (values.pendingAttachmentFiles && values.pendingAttachmentFiles.length > 0) {
				uploadedAttachmentKeys = await uploadBatch(values.pendingAttachmentFiles);
			}
			const allAttachments = [...(values.attachments || []), ...uploadedAttachmentKeys];

			values.startDate = moment(values.startDate)
				.startOf('day')
				.add(moment.duration(values.startTime).asSeconds(), 'seconds')
				.toDate();

			values.endDate = moment(values.startDate)
				.startOf('day')
				.add(moment.duration(values.endTime).asSeconds(), 'seconds')
				.toDate();

			const payload = {
				...values,
				attachments: allAttachments as unknown as any[][],
			};

			await updateOppo(opportunityData.id, payload);
			setIsLoading(false);
			setIsSubmitted(true);
		} catch (error: any) {
			setIsLoading(false);
			setOppoError(error?.response?.message || error.message, error.code);
		}
	};

	if (error.state)
		return (
			<ResponseFailure
				styleTable={true}
				message={error.message ? error.message : 'Something went wrong. Please try again'}
				buttonClickHandler={resetOppoError}
				buttonTitle="Try again"
			/>
		);
	if (isSubmitted)
		return <PopupCongrats setIsShowPopup={setIsShowUpdateOpportunityPopup} setIsSubmited={setIsSubmitted} />;
	return (
		<>
			<h2 className="title">Opportunity info</h2>
			<OpportunityForm
				isLoading={isLoading || isUploadingAttachments}
				initialValues={initialValues}
				submitClickHandler={submitClickHandler as any}
				statusClose={opportunityData.status === OPPORTUNITY_STATUSES.CLOSED}
			/>
			<UploadProgressSnackbar open={isUploadingAttachments} items={batchUploadItems} />
		</>
	);
};

const mapStateToProps = (store: Store) => ({
	error: selectorGetError(store),
	userId: selectorGetUserId(store),
});

const mapDispatchToProps = {
	updateOppo: updateOpportunity,
	setOppoError: setError,
	resetOppoError: resetError,
	createOpportunity,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PopupUpdateProps = ConnectedProps<typeof connector> & PopupUpdateParentProps;

export default connector(PopupUpdate);
